import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, EditIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { Tabs } from '../components/ui/Tabs';
import { StatusBadge } from '../components/StatusBadge';
import { OrderTimeline } from '../components/OrderTimeline';
import { OrderDocuments } from '../components/OrderDocuments';
import { OrderNotes } from '../components/OrderNotes';
import { mockOrders, mockStatusHistory, mockDocuments, mockNotes, mockPayoffCompanies, mockInsuranceCompanies, mockAgentContacts, mockTitleCompanies, mockTitleContacts, mockLenders } from '../data/mockData';
import { OrderStatus, DocumentFolder } from '../types';
export function OrderDetail() {
  const {
    orderId
  } = useParams();
  const navigate = useNavigate();
  const order = mockOrders.find(o => o.id === orderId);
  const orderHistory = mockStatusHistory.filter(h => h.orderId === orderId);
  const orderDocuments = mockDocuments.filter(d => d.orderId === orderId);
  const orderNotes = mockNotes.filter(n => n.orderId === orderId);
  const [currentStatus, setCurrentStatus] = useState<OrderStatus>(order?.status || 'ordered');
  if (!order) {
    return <div className="text-center py-12" data-id="element-433">
        <p className="text-slate-600" data-id="element-434">Order not found</p>
        <Button variant="secondary" onClick={() => navigate('/orders')} className="mt-4" data-id="element-435">
          Back to Orders
        </Button>
      </div>;
  }
  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const statusOptions = [{
    value: 'ordered',
    label: 'Ordered'
  }, {
    value: 'pending',
    label: 'Pending'
  }, {
    value: 'received',
    label: 'Received'
  }, {
    value: 'reviewed',
    label: 'Reviewed'
  }, {
    value: 'complete',
    label: 'Complete'
  }];
  const handleStatusChange = (newStatus: OrderStatus) => {
    setCurrentStatus(newStatus);
  };
  const handleDocumentUpload = (file: File, folder: DocumentFolder) => {
    console.log('Uploading file:', file.name, 'to folder:', folder);
  };
  const handleDocumentDelete = (documentId: string) => {
    console.log('Deleting document:', documentId);
  };
  const handleAddNote = (content: string) => {
    console.log('Adding note:', content);
  };
  const payoffCompany = order.payoffCompanyId ? mockPayoffCompanies.find(c => c.id === order.payoffCompanyId) : null;
  const insuranceCompany = order.insuranceCompanyId ? mockInsuranceCompanies.find(c => c.id === order.insuranceCompanyId) : null;
  const agentContact = order.agentContactId ? mockAgentContacts.find(a => a.id === order.agentContactId) : null;
  const chosenLender = order.chosenLenderId ? mockLenders.find(l => l.id === order.chosenLenderId) : null;
  const effectiveDateLabel = order.loanType === 'Purchase' ? 'HOI Effective Date (Close Date)' : 'HOI Effective Date (Ordered Date)';
  const titleCompany = order.titleCompanyId ? mockTitleCompanies.find(c => c.id === order.titleCompanyId) : null;
  const titleContact = order.titleContactId ? mockTitleContacts.find(c => c.id === order.titleContactId) : null;
  const titleLender = order.orderType === 'title' && order.chosenLenderId ? mockLenders.find(l => l.id === order.chosenLenderId) : null;
  const tabs = [{
    id: 'timeline',
    label: 'Timeline',
    content: <OrderTimeline history={orderHistory} data-id="element-436" />
  }, {
    id: 'documents',
    label: 'Documents',
    content: <OrderDocuments documents={orderDocuments} onUpload={handleDocumentUpload} onDelete={handleDocumentDelete} data-id="element-437" />
  }, {
    id: 'notes',
    label: 'Notes',
    content: <OrderNotes notes={orderNotes} onAddNote={handleAddNote} data-id="element-438" />
  }];
  return <div className="space-y-6" data-id="element-439">
      <div className="flex items-center justify-between" data-id="element-440">
        <div className="flex items-center space-x-4" data-id="element-441">
          <Button variant="ghost" onClick={() => navigate('/orders')} className="p-2" data-id="element-442">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-443" />
          </Button>
          <div data-id="element-444">
            <h1 className="text-3xl font-bold text-slate-900" data-id="element-445">
              {order.loanNumber}
            </h1>
            <p className="text-slate-600 mt-1" data-id="element-446">{order.borrowerName}</p>
          </div>
        </div>
        <Button variant="secondary" data-id="element-447">
          <EditIcon className="w-4 h-4 mr-2" data-id="element-448" />
          Edit Order
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-449">
        <div className="lg:col-span-2 space-y-6" data-id="element-450">
          <Card className="p-6" data-id="element-451">
            <h2 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-452">
              Order Details
            </h2>
            <div className="grid grid-cols-2 gap-4" data-id="element-453">
              <div data-id="element-454">
                <p className="text-sm text-slate-600 mb-1" data-id="element-455">Order Type</p>
                <p className="text-sm font-medium text-slate-900 capitalize" data-id="element-456">
                  {order.orderType}
                </p>
              </div>
              <div data-id="element-457">
                <p className="text-sm text-slate-600 mb-1" data-id="element-458">Status</p>
                <StatusBadge status={currentStatus} data-id="element-459" />
              </div>
              <div data-id="element-460">
                <p className="text-sm text-slate-600 mb-1" data-id="element-461">Assigned To</p>
                <p className="text-sm font-medium text-slate-900" data-id="element-462">
                  {order.assignedTo}
                </p>
              </div>
              <div data-id="element-463">
                <p className="text-sm text-slate-600 mb-1" data-id="element-464">Created</p>
                <p className="text-sm font-medium text-slate-900" data-id="element-465">
                  {formatDate(order.createdAt)}
                </p>
              </div>
            </div>

            {order.orderType === 'payoff' && <div className="mt-6 pt-6 border-t border-slate-200" data-id="element-466">
                <h3 className="text-md font-medium text-slate-900 mb-3" data-id="element-467">
                  Payoff Information
                </h3>

                {/* Dates */}
                <div className="grid grid-cols-3 gap-4 mb-4" data-id="element-468">
                  <div data-id="element-469">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-470">Ordered Date</p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-471">
                      {formatDate(order.payoffOrderedDate || '')}
                    </p>
                  </div>
                  <div data-id="element-472">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-473">
                      Completed Date
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-474">
                      {formatDate(order.payoffCompletedDate || '')}
                    </p>
                  </div>
                  <div data-id="element-475">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-476">
                      Good Thru Date
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-477">
                      {formatDate(order.goodThroughDate || '')}
                    </p>
                  </div>
                </div>

                {/* Loan Details */}
                <div className="grid grid-cols-3 gap-4 mb-4" data-id="element-478">
                  <div data-id="element-479">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-480">
                      Existing Loan #
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-481">
                      {order.existingLoanNumber || '—'}
                    </p>
                  </div>
                  <div data-id="element-482">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-483">Lien Position</p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-484">
                      {order.lienPosition || '—'}
                    </p>
                  </div>
                  <div data-id="element-485">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-486">
                      Liability Type
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-487">
                      {order.liabilityType || '—'}
                    </p>
                  </div>
                </div>

                {/* Payoff Company */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-488">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3" data-id="element-489">
                    Payoff Company
                  </p>
                  <div className="grid grid-cols-2 gap-4" data-id="element-490">
                    <div data-id="element-491">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-492">Company</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-493">
                        {payoffCompany?.name || '—'}
                      </p>
                    </div>
                    <div data-id="element-494">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-495">
                        Is Automated
                      </p>
                      {order.isAutomated ? <span className="inline-flex items-center text-sm font-medium text-emerald-700" data-id="element-496">
                          <CheckCircleIcon className="w-4 h-4 mr-1" data-id="element-497" />
                          Yes
                        </span> : <span className="inline-flex items-center text-sm font-medium text-slate-500" data-id="element-498">
                          <XCircleIcon className="w-4 h-4 mr-1" data-id="element-499" />
                          No
                        </span>}
                    </div>
                    <div data-id="element-500">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-501">Phone</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-502">
                        {order.payoffCompanyPhone || '—'}
                      </p>
                    </div>
                    <div data-id="element-503">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-504">Email</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-505">
                        {order.payoffCompanyEmail || '—'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Borrowers Auth Status */}
                <div className="mt-4" data-id="element-506">
                  <p className="text-sm text-slate-600 mb-1" data-id="element-507">
                    Borrowers Authorization
                  </p>
                  {order.borrowersAuthAttached ? <span className="inline-flex items-center text-sm font-medium text-emerald-700" data-id="element-508">
                      <CheckCircleIcon className="w-4 h-4 mr-1" data-id="element-509" />
                      Attached
                    </span> : <span className="inline-flex items-center text-sm font-medium text-amber-600" data-id="element-510">
                      <XCircleIcon className="w-4 h-4 mr-1" data-id="element-511" />
                      Not attached — required before send
                    </span>}
                </div>
              </div>}

            {order.orderType === 'insurance' && <div className="mt-6 pt-6 border-t border-slate-200" data-id="element-512">
                <h3 className="text-md font-medium text-slate-900 mb-3" data-id="element-513">
                  Insurance Information
                </h3>

                {/* Type & Loan Type */}
                <div className="grid grid-cols-3 gap-4 mb-4" data-id="element-514">
                  <div data-id="element-515">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-516">
                      Insurance Type
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-517">
                      {order.insuranceType}
                    </p>
                  </div>
                  <div data-id="element-518">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-519">Loan Type</p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-520">
                      {order.loanType || '—'}
                    </p>
                  </div>
                  <div data-id="element-521">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-522">
                      {effectiveDateLabel}
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-523">
                      {formatDate(order.hoiEffectiveDate || '')}
                    </p>
                  </div>
                </div>

                {/* Insurance Company */}
                <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-524">
                  <div data-id="element-525">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-526">
                      Insurance Company
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-527">
                      {insuranceCompany?.name || '—'}
                    </p>
                  </div>
                  {order.policyNumber && <div data-id="element-528">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-529">
                        Policy Number
                      </p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-530">
                        {order.policyNumber}
                      </p>
                    </div>}
                </div>

                {/* Agent Contact */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 mb-4" data-id="element-531">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3" data-id="element-532">
                    Agent Contact
                  </p>
                  <div className="grid grid-cols-3 gap-4" data-id="element-533">
                    <div data-id="element-534">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-535">Name</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-536">
                        {agentContact?.name || '—'}
                      </p>
                    </div>
                    <div data-id="element-537">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-538">Phone</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-539">
                        {order.agentPhone || '—'}
                      </p>
                    </div>
                    <div data-id="element-540">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-541">Email</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-542">
                        {order.agentEmail || '—'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Mortgagee Clause */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 mb-4" data-id="element-543">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2" data-id="element-544">
                    Mortgagee Clause
                  </p>
                  <p className="text-sm font-medium text-slate-900" data-id="element-545">
                    {order.mortgageeClause || '—'}
                  </p>
                  {chosenLender && <p className="text-xs text-slate-500 mt-1" data-id="element-546">
                      From: {chosenLender.name}
                    </p>}
                </div>

                {/* Borrowers Auth Status */}
                <div data-id="element-547">
                  <p className="text-sm text-slate-600 mb-1" data-id="element-548">
                    Borrowers Authorization
                  </p>
                  {order.insuranceBorrowersAuthAttached ? <span className="inline-flex items-center text-sm font-medium text-emerald-700" data-id="element-549">
                      <CheckCircleIcon className="w-4 h-4 mr-1" data-id="element-550" />
                      Attached
                    </span> : <span className="inline-flex items-center text-sm font-medium text-amber-600" data-id="element-551">
                      <XCircleIcon className="w-4 h-4 mr-1" data-id="element-552" />
                      Not attached — required before send
                    </span>}
                </div>
              </div>}

            {order.orderType === 'title' && <div className="mt-6 pt-6 border-t border-slate-200" data-id="element-553">
                <h3 className="text-md font-medium text-slate-900 mb-3" data-id="element-554">
                  Title Information
                </h3>

                {/* Opportunity Fields */}
                <div className="grid grid-cols-2 gap-4 mb-4 bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-555">
                  <div data-id="element-556">
                    <p className="text-xs font-medium text-slate-500 mb-1" data-id="element-557">
                      DOB (from Opportunity)
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-558">
                      {formatDate(order.borrowerDob || '')}
                    </p>
                  </div>
                  <div data-id="element-559">
                    <p className="text-xs font-medium text-slate-500 mb-1" data-id="element-560">
                      Last 4 SSN (from Opportunity)
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-561">
                      {order.borrowerLast4SSN ? `••••${order.borrowerLast4SSN}` : '—'}
                    </p>
                  </div>
                </div>

                {/* Mortgagee Clause */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 mb-4" data-id="element-562">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2" data-id="element-563">
                    Mortgagee Clause
                  </p>
                  <p className="text-sm font-medium text-slate-900" data-id="element-564">
                    {order.titleMortgageeClause || '—'}
                  </p>
                  {titleLender && <p className="text-xs text-slate-500 mt-1" data-id="element-565">
                      From: {titleLender.name}
                    </p>}
                </div>

                {/* Title Company */}
                <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-566">
                  <div data-id="element-567">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-568">Title Company</p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-569">
                      {titleCompany?.name || '—'}
                    </p>
                  </div>
                </div>

                {/* Title Contact */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-570">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3" data-id="element-571">
                    Title Contact
                  </p>
                  <div className="grid grid-cols-3 gap-4" data-id="element-572">
                    <div data-id="element-573">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-574">Name</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-575">
                        {titleContact?.name || '—'}
                      </p>
                    </div>
                    <div data-id="element-576">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-577">Phone</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-578">
                        {order.titleContactPhone || '—'}
                      </p>
                    </div>
                    <div data-id="element-579">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-580">Email</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-581">
                        {order.titleContactEmail || '—'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>}

            {order.orderType === 'appraisal' && <div className="mt-6 pt-6 border-t border-slate-200" data-id="element-582">
                <h3 className="text-md font-medium text-slate-900 mb-3" data-id="element-583">
                  Appraisal Information
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-584">
                  <div data-id="element-585">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-586">Appraiser</p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-587">
                      {order.appraiser}
                    </p>
                  </div>
                  {order.appraisalOrderDate && <div data-id="element-588">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-589">Order Date</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-590">
                        {formatDate(order.appraisalOrderDate)}
                      </p>
                    </div>}
                  {order.propertyAddress && <div className="col-span-2" data-id="element-591">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-592">
                        Property Address
                      </p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-593">
                        {order.propertyAddress}
                      </p>
                    </div>}
                  {order.valuLinkId && <div data-id="element-594">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-595">ValuLink ID</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-596">
                        {order.valuLinkId}
                      </p>
                    </div>}
                </div>
              </div>}
          </Card>

          <Card className="p-6" data-id="element-597">
            <Tabs tabs={tabs} data-id="element-598" />
          </Card>
        </div>

        <div className="space-y-6" data-id="element-599">
          <Card className="p-6" data-id="element-600">
            <h2 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-601">
              Update Status
            </h2>
            <Select options={statusOptions} value={currentStatus} onChange={e => handleStatusChange(e.target.value as OrderStatus)} data-id="element-602" />
            <p className="text-xs text-slate-500 mt-2" data-id="element-603">
              Status changes are automatically timestamped and tracked
            </p>
          </Card>

          <Card className="p-6" data-id="element-604">
            <h2 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-605">
              Quick Stats
            </h2>
            <div className="space-y-3" data-id="element-606">
              <div className="flex justify-between items-center" data-id="element-607">
                <span className="text-sm text-slate-600" data-id="element-608">Documents</span>
                <span className="text-sm font-medium text-slate-900" data-id="element-609">
                  {orderDocuments.length}
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-610">
                <span className="text-sm text-slate-600" data-id="element-611">Notes</span>
                <span className="text-sm font-medium text-slate-900" data-id="element-612">
                  {orderNotes.length}
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-613">
                <span className="text-sm text-slate-600" data-id="element-614">Status Updates</span>
                <span className="text-sm font-medium text-slate-900" data-id="element-615">
                  {orderHistory.length}
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>;
}